import { localizeEventKey } from "@/lib/renata/experience/events";
/**
 * Renata OS — Actividad (/app/renata/timeline)
 * Unified business timeline: orders, memory, AI actions, insights, task events, mission events.
 */
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Activity, RefreshCw, TrendingUp, Bot, Lightbulb,
  Zap, Target, Brain, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";

export const Route = createFileRoute("/app/renata/timeline")({
  component: RenataTimelinePage,
  head: () => ({ meta: [{ title: "Actividad — Renata — Structura Market" }] }),
});

interface ActividadEvent {
  id: string;
  occurred_at: string;
  type: string;
  title: string;
  description?: string;
  meta?: Record<string, unknown>;
}

const TYPE_CONFIG: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; color: string; label: string; bg: string }
> = {
  order_spike:       { icon: TrendingUp, color: "text-blue-600",    label: "Ventas",      bg: "bg-blue-100 dark:bg-blue-950/40" },
  new_customer:      { icon: Activity,   color: "text-green-600",   label: "Cliente",     bg: "bg-green-100 dark:bg-green-950/40" },
  stock_alert:       { icon: Activity,   color: "text-amber-600",   label: "Stock",       bg: "bg-amber-100 dark:bg-amber-950/40" },
  promo_started:     { icon: Zap,        color: "text-violet-600",  label: "Promo",       bg: "bg-violet-100 dark:bg-violet-950/40" },
  promo_ended:       { icon: Zap,        color: "text-slate-500",   label: "Promo",       bg: "bg-slate-100 dark:bg-slate-900/40" },
  insight_critical:  { icon: Lightbulb,  color: "text-amber-600",   label: "Oportunidad", bg: "bg-amber-100 dark:bg-amber-950/40" },
  ai_action:         { icon: Bot,        color: "text-violet-600",  label: "Renata",      bg: "bg-violet-100 dark:bg-violet-950/40" },
  cron_event:        { icon: Clock,      color: "text-slate-500",   label: "Automático",  bg: "bg-slate-100 dark:bg-slate-900/40" },
  memory_event:      { icon: Brain,      color: "text-emerald-600", label: "Memoria",     bg: "bg-emerald-100 dark:bg-emerald-950/40" },
  memory_miss:       { icon: Brain,      color: "text-slate-500",   label: "Memoria",     bg: "bg-slate-100 dark:bg-slate-900/40" },
  memory_hit:        { icon: Brain,      color: "text-emerald-600", label: "Memoria",     bg: "bg-emerald-100 dark:bg-emerald-950/40" },
  task_event:        { icon: Zap,        color: "text-primary",     label: "Acción",      bg: "bg-violet-100 dark:bg-violet-950/40" },
  mission_event:     { icon: Target,     color: "text-indigo-600",  label: "Plan",        bg: "bg-indigo-100 dark:bg-indigo-950/40" },
  session_started:   { icon: Activity,   color: "text-blue-500",    label: "Sesión",      bg: "bg-blue-100 dark:bg-blue-950/40" },
  session_ended:     { icon: Activity,   color: "text-slate-400",   label: "Sesión",      bg: "bg-slate-100 dark:bg-slate-900/40" },
  checkout_started:  { icon: Activity,   color: "text-green-600",   label: "Compra",      bg: "bg-green-100 dark:bg-green-950/40" },
  apply_field:       { icon: Bot,        color: "text-violet-600",  label: "Renata",      bg: "bg-violet-100 dark:bg-violet-950/40" },
  task_proposed:     { icon: Zap,        color: "text-violet-600",  label: "Acción",      bg: "bg-violet-100 dark:bg-violet-950/40" },
  task_completed:    { icon: Zap,        color: "text-emerald-600", label: "Acción",      bg: "bg-emerald-100 dark:bg-emerald-950/40" },
  approval_requested:{ icon: Clock,      color: "text-amber-600",   label: "Aprobación",  bg: "bg-amber-100 dark:bg-amber-950/40" },
  approval_accepted: { icon: Activity,   color: "text-emerald-600", label: "Aprobación",  bg: "bg-emerald-100 dark:bg-emerald-950/40" },
  approval_rejected: { icon: Activity,   color: "text-red-500",     label: "Aprobación",  bg: "bg-red-100 dark:bg-red-950/40" },
  knowledge_hit:     { icon: Brain,      color: "text-emerald-600", label: "Biblioteca",  bg: "bg-emerald-100 dark:bg-emerald-950/40" },
  knowledge_miss:    { icon: Brain,      color: "text-slate-500",   label: "Biblioteca",  bg: "bg-slate-100 dark:bg-slate-900/40" },
  learning_candidate:{ icon: Brain,      color: "text-amber-600",   label: "Aprendizaje", bg: "bg-amber-100 dark:bg-amber-950/40" },
  handoff_opened:    { icon: Activity,   color: "text-sky-600",     label: "Atención",    bg: "bg-sky-100 dark:bg-sky-950/40" },
  system:            { icon: Activity,   color: "text-muted-foreground", label: "Sistema", bg: "bg-muted" },
};

/** Normaliza el tipo de evento: extrae el key puro si viene como "Evento: session_started". */
function normalizeEventType(type: string): string {
  // Backend a veces manda prefijos como "Evento: ", "Memoria: ", "Acción Renata: "
  const cleaned = type.replace(/^[^:]+:\s*/, "").replace(/\s*\(.*\)$/, "").trim();
  return cleaned || type;
}

function getTypeConfig(rawType: string) {
  const type = normalizeEventType(rawType);
  return TYPE_CONFIG[type] ?? TYPE_CONFIG[rawType] ?? TYPE_CONFIG.system;
}

const SUFFIX_ES: Record<string, string> = {
  "(cancelada)": "(cancelado)",
  "(cancelado)": "(cancelado)",
  "(cancelled)": "(cancelado)",
  "(failed)": "(falló)",
  "(success)": "(completado)",
  "(completed)": "(completado)",
  "(error)": "(con error)",
  "(timeout)": "(tiempo agotado)",
  "(skipped)": "(omitido)",
};

function translateSuffix(raw: string): string {
  const lower = raw.toLowerCase().trim();
  for (const [k, v] of Object.entries(SUFFIX_ES)) {
    if (lower === k) return " " + v;
  }
  return " " + raw;
}

/**
 * Convierte el título del evento a español legible.
 * Maneja tres formatos del backend:
 *   "Prefijo: key"            → "Prefijo: session_started" → "Sesión iniciada"
 *   "Prefijo: key (status)"   → "Acción Renata: apply_field (cancelada)" → "Renata aplicó un cambio (cancelado)"
 *   "Key sin prefijo"         → "Apply field (cancelado)" → "Renata aplicó un cambio (cancelado)"
 */
function humanizeTitle(title: string, _type: string): string {
  // Caso 1: "Prefijo: key" o "Prefijo: key (status)"
  const colonMatch = /^[^:]+:\s*(.+?)(\s*\([^)]+\))?$/.exec(title);
  if (colonMatch) {
    const key = colonMatch[1].trim().replace(/\s+/g, "_").toLowerCase();
    const rawSuffix = colonMatch[2] ?? "";
    const translated = localizeEventKey(key);
    if (translated !== key) {
      const esSuffix = rawSuffix ? translateSuffix(rawSuffix) : "";
      return translated + esSuffix;
    }
    // No match en EVENT_LABELS — intentar sin underscore conversion
    const translatedRaw = localizeEventKey(colonMatch[1].trim());
    if (translatedRaw !== colonMatch[1].trim()) {
      return translatedRaw + (rawSuffix ? translateSuffix(rawSuffix) : "");
    }
  }

  // Caso 2: "Some words (status)" sin prefijo colon — ej. "Apply field (cancelado)"
  const noColonMatch = /^(.+?)(\s*\([^)]+\))?$/.exec(title);
  if (noColonMatch) {
    const rawKey = noColonMatch[1].trim().replace(/\s+/g, "_").toLowerCase();
    const rawSuffix = noColonMatch[2] ?? "";
    const translated = localizeEventKey(rawKey);
    if (translated !== rawKey) {
      return translated + (rawSuffix ? translateSuffix(rawSuffix) : "");
    }
    // También intentar directo si ya es snake_case
    const direct = localizeEventKey(noColonMatch[1].trim());
    if (direct !== noColonMatch[1].trim()) {
      return direct + (rawSuffix ? translateSuffix(rawSuffix) : "");
    }
  }

  // Fallback: quitar guiones bajos y capitalizar
  return title.replace(/_/g, " ");
}

function TimelineItem({ event }: { event: ActividadEvent }) {
  const cfg = getTypeConfig(event.type);
  const Icon = cfg.icon;
  const displayTitle = humanizeTitle(event.title, event.type);

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={cn("h-8 w-8 rounded-full flex items-center justify-center shrink-0", cfg.bg)}>
          <Icon className={cn("h-4 w-4", cfg.color)} />
        </div>
        <div className="w-px flex-1 bg-border mt-1" />
      </div>

      <div className="flex-1 pb-4 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge
                variant="outline"
                className={cn("text-[10px] shrink-0", cfg.bg, cfg.color, "border-0")}
              >
                {cfg.label}
              </Badge>
              <p className="text-sm font-medium leading-tight">{displayTitle}</p>
            </div>
            {event.description && (
              <p className="text-xs text-muted-foreground mt-0.5">{event.description}</p>
            )}
          </div>
          <time className="text-xs text-muted-foreground shrink-0">
            {new Date(event.occurred_at).toLocaleString("es-AR", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
          </time>
        </div>
      </div>
    </div>
  );
}

type FilterType = "all" | string;

const FILTER_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "order_spike", label: "Ventas" },
  { value: "ai_action", label: "AI" },
  { value: "task_event", label: "Acciones" },
  { value: "mission_event", label: "Planes de trabajo" },
  { value: "insight_critical", label: "Oportunidades" },
  { value: "memory_event", label: "Memoria" },
];

function RenataTimelinePage() {
  const tenantId = useWorkspaceTenantId();
  const [events, setEvents] = useState<ActividadEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>("all");

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await fetch("/api/renata/timeline?limit=50", {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      if (res.ok) {
        const data = await res.json() as { events: ActividadEvent[] };
        setEvents(data.events ?? []);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Record timeline_opened OS event (best-effort, fire-and-forget)
    if (tenantId) {
      void fetch("/api/renata/missions?os_event=timeline_opened", {
        method: "POST",
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      }).catch(() => {});
    }
  }, [tenantId]);

  const filtered = filter === "all"
    ? events
    : events.filter((e) => e.type === filter);

  // Group by date
  const grouped = filtered.reduce<Record<string, ActividadEvent[]>>((acc, ev) => {
    const day = new Date(ev.occurred_at).toLocaleDateString("es-AR", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
    (acc[day] ??= []).push(ev);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-base font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-500" />
            Actividad del negocio
          </h1>
          <p className="text-sm text-muted-foreground">
            Últimos 50 eventos · últimos 30 días
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2">
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          Actualizar
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-1 flex-wrap">
        {FILTER_OPTIONS.map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? "default" : "outline"}
            size="sm"
            className={cn("h-7 text-xs", filter === f.value && "bg-blue-600 hover:bg-blue-700")}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading && (
        <div className="flex h-32 items-center justify-center">
          <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Activity className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No hay eventos para este filtro.
          </p>
        </div>
      )}

      {/* Actividad */}
      {Object.entries(grouped).map(([day, dayEvents]) => (
        <div key={day} className="space-y-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3 capitalize">
            {day}
          </p>
          {dayEvents.map((ev) => (
            <TimelineItem key={ev.id} event={ev} />
          ))}
        </div>
      ))}
    </div>
  );
}
